import {
  createContext,
  type ReactNode,
  useEffect,
  useId,
  useState
} from 'react'
import * as Mtds from '@mattilsynet/design/react'
import { ModalDialog } from '../modal-dialog/ModalDialog'

type DetailsLayoutProps = {
  children: ReactNode
  details: ReactNode
  detailsLabel?: string
  mainMaxWidth?: number
  stickyDetails?: boolean
  useMobileDetailsDrawer?: boolean
}

export const DetailsLayoutActionContext = createContext<ReactNode>(null)

const sidebarCollapsePath =
  'M4.38 4.38h19.25a1.75 1.75 0 0 1 1.75 1.75v15.75a1.75 1.75 0 0 1-1.75 1.75H4.38a1.75 1.75 0 0 1-1.75-1.75V6.13a1.75 1.75 0 0 1 1.75-1.75Zm4.37 1.75H4.37v15.75h4.38V6.13Zm1.75 15.75h13.13V6.13H10.5v15.75Zm5.16-3.48-3.93-3.94a.65.65 0 0 1 0-.93l3.93-3.93a.66.66 0 1 1 .93.93l-2.81 2.81h8.03a.66.66 0 0 1 0 1.32h-8.03l2.81 2.81a.66.66 0 1 1-.93.93Z'
const sidebarExpandPath =
  'M4.38 4.38h19.25a1.75 1.75 0 0 1 1.75 1.75v15.75a1.75 1.75 0 0 1-1.75 1.75H4.38a1.75 1.75 0 0 1-1.75-1.75V6.13a1.75 1.75 0 0 1 1.75-1.75Zm4.37 1.75H4.37v15.75h4.38V6.13Zm1.75 15.75h13.13V6.13H10.5v15.75Zm7.84-3.48 3.94-3.94a.66.66 0 0 0 0-.93L18.34 9.6a.66.66 0 0 0-.93.93l2.82 2.81h-8.04a.66.66 0 0 0 0 1.32h8.04l-2.82 2.81a.66.66 0 0 0 .93.93Z'

export const DetailsLayout = ({
  children,
  details,
  detailsLabel = 'Detaljer',
  mainMaxWidth = 800,
  stickyDetails = true,
  useMobileDetailsDrawer = false
}: DetailsLayoutProps) => {
  const dialogId = useId()
  const detailsId = useId()
  const [showDetails, setShowDetails] = useState(true)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isWideScreen, setIsWideScreen] = useState(
    () => globalThis.matchMedia?.('(min-width: 96rem)').matches ?? false
  )
  const buttonLabel = showDetails
    ? `Skjul ${detailsLabel}`
    : `Vis ${detailsLabel}`
  const isDetailsOpen = isWideScreen ? showDetails : isDrawerOpen
  const controlsId = isWideScreen ? detailsId : dialogId
  const detailsAction =
    isWideScreen || useMobileDetailsDrawer ? (
      <Mtds.Button
        type='button'
        aria-label={buttonLabel}
        data-tooltip={buttonLabel}
        onClick={() => {
          if (isWideScreen) {
            setShowDetails((prev) => !prev)
            return
          }
          setIsDrawerOpen(true)
        }}
        aria-expanded={isDetailsOpen}
        aria-controls={controlsId}
      >
        <svg
          aria-hidden
          className='size-[var(--mtds-icon-size)] rotate-180'
          viewBox='0 0 28 28'
          fill='currentColor'
        >
          <path d={isDetailsOpen ? sidebarCollapsePath : sidebarExpandPath} />
        </svg>
      </Mtds.Button>
    ) : null

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia?.('(min-width: 96rem)')
    if (!mediaQuery) return

    const handleChange = () => {
      setIsWideScreen(mediaQuery.matches)
      if (mediaQuery.matches) setIsDrawerOpen(false)
    }

    handleChange()
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const detailsContent = (
    <aside
      id={detailsId}
      aria-label={detailsLabel}
      aria-hidden={isWideScreen && !showDetails ? true : undefined}
      inert={isWideScreen && !showDetails ? true : undefined}
      className={`min-w-0 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none 2xl:w-[480px] 2xl:pl-5 ${
        isWideScreen && !showDetails
          ? '2xl:pointer-events-none 2xl:-translate-x-2 2xl:opacity-0'
          : '2xl:translate-x-0 2xl:opacity-100'
      }`}
    >
      <div className={stickyDetails ? '2xl:sticky 2xl:top-6' : undefined}>
        {details}
      </div>
    </aside>
  )

  return (
    <DetailsLayoutActionContext.Provider value={detailsAction}>
      <Mtds.Grid
        className={`w-full gap-8 transition-[gap,grid-template-columns] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
          showDetails
            ? '2xl:grid-cols-[minmax(0,1fr)_480px] 2xl:gap-5'
            : '2xl:grid-cols-[minmax(0,1fr)_0px] 2xl:gap-0'
        }`}
      >
        <Mtds.Grid
          data-gap={8}
          style={{ maxWidth: mainMaxWidth }}
          className='mx-auto w-full min-w-0 content-start'
        >
          {children}
        </Mtds.Grid>
        {isWideScreen && (
          <div className='min-h-full min-w-0 self-stretch overflow-hidden'>
            {detailsContent}
          </div>
        )}
        {!isWideScreen && !useMobileDetailsDrawer && (
          <Mtds.Grid
            data-gap={4}
            style={{ maxWidth: mainMaxWidth }}
            className='mx-auto w-full min-w-0'
          >
            {detailsContent}
          </Mtds.Grid>
        )}
        {!isWideScreen && useMobileDetailsDrawer && (
          <ModalDialog
            id={dialogId}
            className='w-full max-w-[480px]'
            closedby='any'
            data-placement='right'
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
          >
            <Mtds.Button
              command='close'
              commandfor={dialogId}
              aria-label='Lukk'
            />
            {detailsContent}
          </ModalDialog>
        )}
      </Mtds.Grid>
    </DetailsLayoutActionContext.Provider>
  )
}
